import { type Currency, formatAmount, parseAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import {
  amountColumnHeader,
  amountInputLabel,
  amountInputStep,
  amountInputValue,
  capitalize,
  useCurrency,
} from "../lib/currency";
import { centsToDollars } from "../lib/format";
import { type trpcClient, useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

type PackRow = Awaited<ReturnType<typeof trpcClient.balancePacks.list.query>>[number];

/**
 * Dialog draft. The price is real money, typed in dollars; the credited amount is typed in
 * the environment's currency. Both land on the wire as cents.
 */
interface PackDraft {
  name: string;
  priceDollars: string;
  credited: string;
  sortOrder: string;
}

const EMPTY_DRAFT: PackDraft = { name: "", priceDollars: "", credited: "", sortOrder: "0" };

function draftFrom(pack: PackRow, currency: Currency): PackDraft {
  return {
    name: pack.name,
    priceDollars: (pack.priceCents / 100).toFixed(2),
    credited: amountInputValue(pack.creditedCents, currency),
    sortOrder: String(pack.sortOrder),
  };
}

/**
 * The balance packs shoppers can buy in this environment, a card on Storefront → Payments
 * beside the switch that offers them. Viewers browse packs; creating, editing and archiving is
 * an editor's action.
 */
export function BalancePacksCard() {
  const trpc = useTRPC();
  const currency = useCurrency();
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const queryClient = useQueryClient();
  // The tenant/environment scope rides the request headers (x-loot-tenant from the
  // route, x-loot-environment from the switcher in the tenant header).
  const packs = useQuery(trpc.balancePacks.list.queryOptions());

  // editing === null → closed; "new" → create; otherwise the pack being edited.
  const [editing, setEditing] = useState<PackRow | "new" | null>(null);
  const [draft, setDraft] = useState<PackDraft>(EMPTY_DRAFT);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.balancePacks.list.queryOptions().queryKey,
    });
  };
  const close = () => setEditing(null);

  const create = useMutation(
    trpc.balancePacks.create.mutationOptions({
      onSuccess: () => {
        invalidate();
        close();
      },
    }),
  );
  const update = useMutation(
    trpc.balancePacks.update.mutationOptions({
      onSuccess: () => {
        invalidate();
        close();
      },
    }),
  );

  const priceCents = Math.round(Number.parseFloat(draft.priceDollars || "0") * 100);
  const creditedCents = parseAmount(draft.credited, currency);
  const sortOrder = Number.parseInt(draft.sortOrder || "0", 10);
  const draftValid =
    draft.name.trim().length > 0 &&
    priceCents >= 50 &&
    creditedCents !== null &&
    creditedCents >= 1;

  const submit = () => {
    if (!draftValid || creditedCents === null) return;
    if (editing === "new") {
      create.mutate({ name: draft.name.trim(), priceCents, creditedCents, sortOrder });
    } else if (editing) {
      update.mutate({
        packId: editing.id,
        name: draft.name.trim(),
        priceCents,
        creditedCents,
        sortOrder,
      });
    }
  };

  const toggleActive = useMutation(
    trpc.balancePacks.update.mutationOptions({ onSuccess: invalidate }),
  );

  return (
    <Card id="balance-packs">
      <CardHeader>
        <CardTitle>{capitalize(currency.name)} bundles</CardTitle>
        <CardDescription>
          Offers shoppers can buy through Stripe checkout beside a deposit, shown in Deposit under
          the amounts: "Spend $50, get $60". The price is what the shopper pays in USD; the credited
          amount is what lands in their balance, more than the price for a bonus. Bundles are
          archived, never deleted, so purchase history keeps its references.
        </CardDescription>
        {canEdit && (
          <CardAction>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                create.reset();
                setDraft(EMPTY_DRAFT);
                setEditing("new");
              }}
            >
              <Plus />
              Add bundle
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>{amountColumnHeader("Credited", currency)}</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {packs.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No bundles yet. Shoppers can still deposit any amount; add a bundle to offer a
                  bonus.
                </TableCell>
              </TableRow>
            )}
            {packs.data?.map((pack) => (
              <TableRow key={pack.id}>
                <TableCell className="font-medium">{pack.name}</TableCell>
                <TableCell>{centsToDollars(pack.priceCents)}</TableCell>
                <TableCell>{formatAmount(pack.creditedCents, currency)}</TableCell>
                <TableCell className="text-muted-foreground">{pack.sortOrder}</TableCell>
                <TableCell>
                  <Badge variant={pack.active ? "default" : "outline"}>
                    {pack.active ? "active" : "archived"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                    <div className="inline-flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          update.reset();
                          setDraft(draftFrom(pack, currency));
                          setEditing(pack);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={toggleActive.isPending}
                        onClick={() =>
                          toggleActive.mutate({
                            packId: pack.id,
                            active: !pack.active,
                          })
                        }
                      >
                        {pack.active ? "Archive" : "Restore"}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add bundle" : "Edit bundle"}</DialogTitle>
            <DialogDescription>
              Price is what the shopper pays; the credited amount lands in their balance.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="pack-name">Name</Label>
              <Input
                id="pack-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Starter bundle"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="pack-price">Price (USD)</Label>
                <Input
                  id="pack-price"
                  type="number"
                  min="0.50"
                  step="0.01"
                  value={draft.priceDollars}
                  onChange={(e) => setDraft({ ...draft, priceDollars: e.target.value })}
                  placeholder="5.00"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pack-credited">{amountInputLabel("Credited", currency)}</Label>
                <Input
                  id="pack-credited"
                  type="number"
                  min={amountInputStep(currency)}
                  step={amountInputStep(currency)}
                  value={draft.credited}
                  onChange={(e) => setDraft({ ...draft, credited: e.target.value })}
                  placeholder={amountInputValue(500, currency)}
                  required
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pack-sort">Sort order</Label>
              <Input
                id="pack-sort"
                type="number"
                min="0"
                step="1"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={!draftValid || create.isPending || update.isPending}>
                {create.isPending || update.isPending
                  ? "Saving…"
                  : editing === "new"
                    ? "Add bundle"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
